import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { CompanyApi } from '@api/companies.api';
import { Company } from '@core/interfaces';
import { Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

@Component({
  selector: 'app-dialog-choose-companies',
  templateUrl: './dialog-choose-companies.component.html',
  styleUrls: ['./dialog-choose-companies.component.scss']
})
export class DialogChooseCompaniesComponent implements OnInit, OnDestroy {

  public searchTerm: FormControl = new FormControl();
  public selectedCompaniesLimit = 4;
  public selectedCompanies: Company[] = [];
  public isLoading: Boolean = false;
  public selectedCompaniesSelection = new SelectionModel<string>(true, []);
  public isLoadedAllTheCompanies: Boolean = false;
  public selectedCompaniesHasChanged: Boolean = false;
  public searchCompanyControl: FormControl = new FormControl();
  public companies: Company[] = [];
  public selectedCompaniesDefault = [];

  @ViewChild('content', { static: true })
  public scrollableContent: ElementRef;

  private searchTermSubscription: Subscription;
  private selectedCompaniesSubscription: Subscription;
  private optionsLimit = 25;
  private options = {
    limit: this.optionsLimit,
    offset: 0,
    orderBy: 'name_asc'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public readonly matDialogRef: MatDialogRef<DialogChooseCompaniesComponent>,
    private readonly companiesApi: CompanyApi,
  ) {
    this.selectedCompaniesSelection.select(...this.data.selectedCompanies.map((company) => company.id));
    this.selectedCompaniesDefault = this.selectedCompaniesSelection.selected;
    this.selectedCompanies = [...this.data.selectedCompanies];
  }

  ngOnInit(): void {
    this.searchCompanies();

    this.scrollableContent.nativeElement.addEventListener('scroll', () => {
      if (this.isOnEndOfThePage() && !this.isLoadedAllTheCompanies) {
        this.loadMoreCompanies();
      }
    });

    this.searchTermSubscription = this.searchTerm.valueChanges
      .pipe(
        debounceTime(500),
        map(() => this.companies = []),
        map(() => this.options.offset = 0),
        map(() => (this.searchTerm.value || '').trim().toLowerCase())
      ).subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchCompanies(searchTerm);
          return;
        }
        this.searchCompanies();
      });

    this.selectedCompaniesSubscription = this.selectedCompaniesSelection.changed.subscribe(() => {
      this.selectedCompaniesHasChanged = this.hasSelectedCompaniesChanged();
    });
  }

  ngOnDestroy(): void {
    this.searchTermSubscription.unsubscribe();
    this.selectedCompaniesSubscription.unsubscribe();
  }

  async searchCompanies(searchTerm?: Record<string, any>): Promise<void> {
    this.isLoading = true;
    const query = { name_contains: searchTerm && searchTerm.name ? searchTerm.name : searchTerm };
    const options = {
      limit: this.options.limit,
      offset: this.options.offset,
      orderBy: this.options.orderBy
    };
    const companies: Company[] = (await this.companiesApi.search(query, options).toPromise()).items;

    this.companies.push(...companies);
    this.isLoading = false;
    if (companies.length < this.optionsLimit) {
      this.isLoadedAllTheCompanies = true;
      return;
    }
    this.isLoadedAllTheCompanies = false;
  }

  toggleCompany(newSelectedCompany: Company): void {
    const alreadySelected = this.selectedCompaniesSelection.isSelected(newSelectedCompany.id);
    const didntReachTheLimit = this.selectedCompaniesSelection.selected.length < this.selectedCompaniesLimit;

    if (!alreadySelected && didntReachTheLimit) {
      this.selectedCompaniesSelection.select(newSelectedCompany.id);
      this.selectedCompanies.push(newSelectedCompany);
    } else {
      this.selectedCompaniesSelection.deselect(newSelectedCompany.id);
      this.selectedCompanies = this.selectedCompanies.filter((selectedCompany) => selectedCompany.id !== newSelectedCompany.id);
    }
  }

  isOnEndOfThePage(): boolean {
    const scrollableElement = this.scrollableContent.nativeElement;
    return Math.abs(scrollableElement.scrollHeight - scrollableElement.clientHeight - scrollableElement.scrollTop) < 1;
  }

  loadMoreCompanies(): void {
    this.options.offset += this.options.limit;
    this.searchCompanies();
  }

  hasSelectedCompaniesChanged(): boolean {
    if (this.selectedCompaniesDefault.length !== this.selectedCompaniesSelection.selected.length) {
      return true;
    }

    for (const alreadySelectedCompany of this.selectedCompaniesDefault) {
      const matchingObj = this.selectedCompaniesSelection.selected.find((newSelectedCompany) => newSelectedCompany === alreadySelectedCompany);
      if (!matchingObj) {
        return true;
      }
    }

    return false;
  }

  compare(): void {
    if (!this.selectedCompaniesHasChanged || this.isLoading) {
      return;
    }
    this.matDialogRef.close({
      data: {
        selectedCompanies: this.selectedCompanies
      }
    });
  }
}
